<template>
  <q-dialog
    ref="dialogRef"
    class="element-info-dialog"
    no-shake
    full-width
    @hide="onDialogHide"
  >
    <q-card class="inhdialog-container element-info-dialog">
      <div class="inhdialog-header">
        <h1 class="dialog_name">
          {{ $t('information_about') }} {{ elementTitle }}
        </h1>
        <q-btn
          flat
          round
          icon="close"
          size="md"
          color="accent"
          :title="$t('close_label')"
          @click="close"
        />
      </div>

      <div class="dialog_content">
        <div v-if="loading" class="loading-container q-mt-sm">
          <div v-for="n in 6" :key="n" class="field-group">
            <div class="field-group__row no-wrap justify-left">
              <div class="skeleton skeleton-title"></div>
              <div
                class="skeleton skeleton-text"
                :class="{
                  'skeleton-text-short': n % 3 === 0,
                  'skeleton-text-medium': n % 3 === 1,
                }"
              ></div>
            </div>
          </div>
        </div>

        <div v-else class="inner_container">
          <div class="field-group">
            <div class="field-group__row no-wrap justify-left">
              <div class="bold-name mr-xs">{{ $t('created_at_label') }}:</div>
              <span>{{ createdAtText }}</span>
              <span v-if="info?.Creator?.Name"> [{{ info.Creator.Name }}]</span>
            </div>
          </div>

          <div class="field-group field-group--path">
            <div class="field-group__row field-group__row_path no-wrap justify-left items-center">
              <div class="bold-name mr-xs">{{ $t('path_label') }}:</div>
              <template v-if="pathSegments.length">
                <template v-for="(seg, idx) in pathSegments" :key="'p_' + idx">
                  <span>{{ seg }}</span>
                  <span v-if="idx < pathSegments.length - 1" class="path-arrow-wrap" aria-hidden="true">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M6 12L10 8L6 4" stroke="#979797" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </span>
                </template>
              </template>
              <span v-else>—</span>
            </div>
          </div>

          <div v-if="views.length" class="field-group">
            <div class="field-group__row no-wrap justify-left">
              <div class="bold-name">{{ $t('views_label') }}</div>
              <span class="ei-total"> {{ $t('total_count_label', { count: totalViews }) }}</span>
            </div>
            <div
              v-for="(v, idx) in views"
              :key="'view_' + idx"
              class="field-group__row no-wrap justify-left ei-log-row"
            >
              <span class="mr-xs ei-datetime">{{ formatDateTime(v.CreateTime) }}</span>
              <span>[{{ v.Creator?.Name }}]</span>
              <span
                v-if="idx === views.length - 1 && viewsRemaining > 0"
                class="ei-more"
                role="button"
                tabindex="0"
                @click="loadMoreViews"
                @keydown.enter="loadMoreViews"
              >{{ viewsMoreLabel }}</span>
            </div>
          </div>

          <div v-if="changes.length" class="field-group">
            <div class="field-group__row no-wrap justify-left">
              <div class="bold-name">{{ $t('changes_label') }}</div>
              <span class="ei-total"> {{ $t('total_count_label', { count: totalChanges }) }}</span>
            </div>
            <div
              v-for="(c, idx) in changes"
              :key="'change_' + idx"
              class="field-group__row no-wrap justify-left ei-log-row"
            >
              <span class="mr-xs ei-datetime">{{ formatDateTime(c.CreateTime) }}</span>
              <span>[{{ c.Creator?.Name }}]</span>
              <span
                v-if="idx === changes.length - 1 && changesRemaining > 0"
                class="ei-more"
                role="button"
                tabindex="0"
                @click="loadMoreChanges"
                @keydown.enter="loadMoreChanges"
              >{{ changesMoreLabel }}</span>
            </div>
          </div>
        </div>
      </div>
    </q-card>
  </q-dialog>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue';
import { useDialogPluginComponent } from 'quasar';
import { BaseListItem } from './interfaces/ListTypes';
import { auxStore } from '@/store/auxillary';
import { AnalyticsElementDetailsDto, AnalyticsActionRecordDto, AnalyticsActionType } from '@/models/interfaces/entity-settings/charts/charts';
import { i18n } from '@/i18n';

const props = defineProps<{
  element: BaseListItem;
}>();

const { dialogRef, onDialogHide, onDialogOK } = useDialogPluginComponent();

// expose required bindings for QDialog
// eslint-disable-next-line vue/no-setup-props-destructure
defineEmits([...useDialogPluginComponent.emits]);

const loading = ref(true);
const info = ref<AnalyticsElementDetailsDto | null>(null);

const store = auxStore();

const initialChunk = 8;
const maxStep = 20;

const elementTitle = computed(() => {
  const el = props.element;
  switch (el.elType) {
    case 7: return i18n.global.t('info_subj_folder');
    case 0: return i18n.global.t('info_subj_selection');
    case 1: return i18n.global.t('info_subj_projection');
    case 2:
    case 3:
    case 4: return i18n.global.t('info_subj_chart');
    case 6: return i18n.global.t('info_subj_list');
    default: return i18n.global.t('info_subj_element');
  }
});

const createdAtText = computed(() => {
  if (!info.value?.CreateTime) return '';
  return formatDateTime(info.value.CreateTime);
});

const pathSegments = computed(() => {
  const sections = info.value?.Path?.Sections;
  const names = sections && Array.isArray(sections)
    ? sections.map((s: any) => s.ElementName)
    : [];
  const currentName = props.element?.Name || '';
  if (names.length) return currentName ? [...names, currentName] : names;
  return currentName ? [currentName] : [];
});

const views = computed<AnalyticsActionRecordDto[]>(() => info.value?.ReadActions || []);
const changes = computed<AnalyticsActionRecordDto[]>(() => info.value?.WriteActions || []);

const totalViews = computed(() =>
  info.value?.ReadActionsTotalCount ?? 0
);
const totalChanges = computed(() =>
  info.value?.WriteActionsTotalCount ?? 0
);

const viewsRemaining = computed(() =>
  Math.max(totalViews.value - views.value.length, 0)
);
const changesRemaining = computed(() =>
  Math.max(totalChanges.value - changes.value.length, 0)
);

const viewsMoreLabel = computed(() => {
  const rem = viewsRemaining.value;
  if (rem <= 0) return '';
  const total = totalViews.value;
  const count = total <= 28 ? rem : Math.min(maxStep, rem);
  return i18n.global.t('more_count_label', { count });
});

const changesMoreLabel = computed(() => {
  const rem = changesRemaining.value;
  if (rem <= 0) return '';
  const total = totalChanges.value;
  const count = total <= 28 ? rem : Math.min(maxStep, rem);
  return i18n.global.t('more_count_label', { count });
});

function formatDateTime(val: string | Date): string {
  if (!val) return '';
  const d = typeof val === 'string' ? new Date(val) : val;
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes} ${day}.${month}.${year}`;
}

async function loadInfo() {
  loading.value = true;
  const { data } = await store.getAnalyticsElementInfo(props.element, {
    readCount: initialChunk,
    writeCount: initialChunk,
  });
  info.value = data || null;
  loading.value = false;
}

async function loadMoreViews() {
  if (viewsRemaining.value <= 0) return;
  const step =
    totalViews.value <= 28
      ? viewsRemaining.value
      : Math.min(maxStep, viewsRemaining.value);
  const { data } = await store.fetchAnalyticsActionsLog(
    props.element,
    AnalyticsActionType.Read,
    views.value.length,
    step,
  );
  if (info.value && Array.isArray(data) && data.length) {
    info.value = {
      ...info.value,
      ReadActions: [...views.value, ...data],
    };
  }
}

async function loadMoreChanges() {
  if (changesRemaining.value <= 0) return;
  const step =
    totalChanges.value <= 28
      ? changesRemaining.value
      : Math.min(maxStep, changesRemaining.value);
  const { data } = await store.fetchAnalyticsActionsLog(
    props.element,
    AnalyticsActionType.Write,
    changes.value.length,
    step,
  );
  if (info.value && Array.isArray(data) && data.length) {
    info.value = {
      ...info.value,
      WriteActions: [...changes.value, ...data],
    };
  }
}

function close() {
  onDialogOK();
}

onMounted(loadInfo);
</script>
<!--
  Глобальный (нескоупленный) блок: q-dialog рендерится через Teleport в body,
  поэтому scoped :deep() из-за отсутствия data-v-xxx в пути не сработает,
  а глобальное правило .q-dialog__inner--fullwidth > div в quasar.scss идёт с !important.
  Перебиваем по классу диалога и тоже с !important.
-->
<style lang="scss">
.element-info-dialog .q-dialog__inner--fullwidth > div {
  min-width: 900px !important;
  width: 900px !important;
  max-width: 900px !important;
}
.element-info-dialog .inhdialog-container {
  width: 100%;
}
</style>

<style scoped lang="scss">
.element-info-dialog {
  line-height: 18px;
  color: $text-color;

  :deep(.dialog_content) {
    padding: 0;
  }

  :deep(.inner_container) {
    padding: 16px 0;
    gap: 16px;
  }

  :deep(.field-group) {
    padding: 0 16px;
  }

  /* Только между «Создано» и «Путь» зазор 8px (16px gap родителя − 8px) */
  :deep(.field-group--path) {
    margin-top: -8px;
  }

  /* Между строками внутри блока: line-height 18px + 4px */
  :deep(.field-group__row + .field-group__row) {
    margin-top: 4px;
  }

  /* Путь — одна строка: без вертикального gap при переносе, только line-height */
  :deep(.field-group__row_path) {
    row-gap: 0;
    line-height: 18px;
  }

  :deep(.ei-datetime) {
    color: $accent;
  }

  :deep(.ei-total) {
    font-weight: normal;
  }

  :deep(.path-arrow-wrap) {
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
  }

  /* Строка лога: контент слева, кнопка «Ещё» справа */
  :deep(.ei-log-row) {
    width: 100%;
  }

  /* Кнопка «Ещё» по макету: таблетка #F2F2F2, текст #979797, прижата к тексту слева */
  :deep(.ei-more) {
    margin-left: 8px;
    padding: 0 4px;
    background: #F2F2F2;
    border-radius: 8px;
    color: #979797;
    font-size: 12px;
    line-height: 16px;
    cursor: pointer;
    flex-shrink: 0;

    &:hover {
      background: #F3F8F4;
      color: #438451;
    }
  }
}
</style>
